// Helpers
const isDefined = v => v !== undefined && v !== null;

const toLowerCaseKeys = (obj) => {
	if (!obj) return obj; // handle null edge case

	if (typeof obj === 'object' && !(obj instanceof Array)) {
		const lowerCaseObj = Object
			.keys(obj)
			.reduce((acc, key) => {
				acc[(key || '').toLowerCase()] = obj[key];
				return acc;
			}, {});
		return lowerCaseObj;
	}

	return obj;
}

/**
 * Attempts to parse a payload body as JSON.
 * - If the input is valid JSON (stringified), it's parsed into an object.
 * - If it's not JSON (e.g. plain string, primitive), the original value is returned.
 * - If undefined/null, returns undefined.
 *
 * @param {string|any} jsonOrNotJson - The body field, possibly JSON encoded.
 * @returns {any} Parsed object, raw string, primitive value, or undefined.
 */
const _parsePayloadBody = (jsonOrNotJson) => {
	let result = isDefined(jsonOrNotJson) ? jsonOrNotJson : undefined;
	try {
		result = JSON.parse(result);
	} catch (err) {
		// do nothing as undefined or a string value which is not json was passed.
	}

	return result;
}



/**
 * Main API:
 * Normalizes a server-sent WebSocket payload into a predictable structure.
 *
 * @param {Object} payloadFromServer - The raw payload from server.
 * @param {any} [payloadFromServer.error] - Optional error field from server.
 * @param {any} [payloadFromServer.isError] - Optional error field from server.
 * @param {string} [payloadFromServer.topic] - PubSub topic the message is tied to.
 * @param {string} [payloadFromServer.action] - Action string from server routing.
 * @param {any} [payloadFromServer.body] - Message body (may be JSON string).
 * @param {any} [payloadFromServer.data] - Alternative to `body` (used by some APIs).
 *
 * @returns {{
 *   error?: any,
 *   isError?: any,
 *   topic?: string,
 *   action?: string,
 *   body?: any
 * }} A normalized payload object.
 */
export const parsePayloadFromServer = (payloadFromServer = {}) => {	
	const lowerCasePayload = toLowerCaseKeys(_parsePayloadBody(payloadFromServer)); // make sure all root keys are lower case
	const { 
		error, 
		topic, 
		action, 
		body: payloadBody, 
		data: payloadData,
		payload: payloadPayload,
	} = lowerCasePayload;

	const body = _parsePayloadBody(payloadBody || payloadData || payloadPayload);
	const lowerCaseBody = toLowerCaseKeys(body); // create ref allowing for lowercase access for fallbacks
	return {
		error: error || lowerCaseBody?.error || undefined,
		topic: topic || lowerCaseBody?.topic || undefined,
		action: action || lowerCaseBody?.action || undefined,
		body,
	}
}